/**
 * ContractAddress — tappable contract address for collection/item headers.
 *
 * Renders a truncated 0x1234…5678 chip. Tapping it opens a two-option menu:
 *   - Copy    → puts the FULL address on the clipboard
 *   - Explorer → opens that chain's block explorer on the address
 *
 * The explorer base URL comes from chainData.json (`exp`, falling back to the
 * legacy `explorer` key) resolved by the collection's chain id. Chains with no
 * configured explorer simply drop the Explorer option — the address is still
 * copyable.
 *
 * SSR-safe: no clipboard or window access during render; the menu only mounts
 * its handlers on interaction.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import chainData from '../../chains';

const isAddress = (v?: string | null): v is string => /^0x[a-fA-F0-9]{40}$/.test((v || '').trim());

/** 0x1234abcd…5678 — enough to recognise, short enough for a header. */
export function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Resolve the explorer base URL for a chain id, or null when the chain has none.
 *
 * `exp` is the documented key; `explorer` is the legacy name still present on
 * a couple of rows. Anvil/local chains deliberately have neither, so they get
 * no Explorer option rather than a dead link.
 */
export function explorerBaseUrl(chainId?: string | number | null): string | null {
  if (chainId === undefined || chainId === null || chainId === '') return null;
  const want = String(chainId);
  const row = (chainData as any[]).find((r) => r && String(r.chainId) === want);
  const base = row?.exp || row?.explorer;
  return typeof base === 'string' && base ? base : null;
}

/** Full explorer URL for an address, or null when the chain has no explorer. */
export function explorerAddressUrl(chainId?: string | number | null, address?: string | null): string | null {
  const base = explorerBaseUrl(chainId);
  if (!base || !isAddress(address)) return null;
  return `${base.replace(/\/+$/, '')}/address/${address}`;
}

interface Props {
  address?: string | null;
  /** Chain id the contract lives on (matches collInfo's `network`). */
  chainId?: string | number | null;
  className?: string;
}

/**
 * Copy via the async clipboard API, falling back to a hidden textarea +
 * execCommand for insecure origins and older browsers, where
 * navigator.clipboard is undefined.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    if (typeof document === 'undefined') return false;
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function ContractAddress({ address, chainId, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  // 'idle' | 'copied' | 'failed' — a silent no-op on failure is the one
  // outcome that must not happen, since the user has no other way to tell
  // whether the copy landed.
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close on outside click / Escape, matching the SearchFilterBar dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => { if (statusTimer.current) clearTimeout(statusTimer.current); }, []);

  const doCopy = useCallback(async () => {
    if (!address) return;
    const ok = await copyText(address);
    setStatus(ok ? 'copied' : 'failed');
    setOpen(false);
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus('idle'), 1800);
  }, [address]);

  const href = explorerAddressUrl(chainId, address);

  // Not a real address (missing, placeholder, or a non-contract collection
  // like ERC-1155 prediction markets) — render nothing rather than a dead chip.
  if (!isAddress(address)) return null;

  return (
    <span className={`contract-addr ${className}`} ref={wrapRef}>
      <button
        type="button"
        className="contract-addr-trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        aria-expanded={open}
        aria-haspopup="menu"
        title={address}
      >
        {truncateAddress(address)}
        <span className="contract-addr-caret" aria-hidden="true">▾</span>
      </button>

      <span
        className="contract-addr-menu"
        role="menu"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(-4px)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <button type="button" role="menuitem" className="contract-addr-item" onClick={doCopy}>
          {status === 'copied' ? 'Copied ✓' : 'Copy address'}
        </button>
        {href && (
          <a
            role="menuitem"
            className="contract-addr-item"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            View on explorer
          </a>
        )}
      </span>

      {status !== 'idle' && (
        <span
          className={`contract-addr-toast${status === 'failed' ? ' is-failed' : ''}`}
          role="status"
        >
          {status === 'copied' ? 'Address copied' : 'Copy failed — use the explorer link'}
        </span>
      )}
    </span>
  );
}

export default ContractAddress;
